import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useDeleteKB } from '@/hooks/useSettings'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface DangerZoneProps {
  kbId: string
  kbName: string
}

export function DangerZone({ kbId, kbName }: DangerZoneProps) {
  const [showDialog, setShowDialog] = useState(false)
  const deleteKB = useDeleteKB(kbId)
  const navigate = useNavigate()
  const { toast } = useToast()

  const handleDelete = async () => {
    try {
      await deleteKB.mutateAsync()
      toast({
        title: 'Knowledge base deleted',
        description: `${kbName} has been permanently deleted`,
      })
      navigate('/app/knowledge')
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete knowledge base',
        variant: 'destructive',
      })
    }
  }

  return (
    <>
      <Card className="border-2 border-destructive">
        <CardHeader>
          <CardTitle className="text-destructive flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            Danger Zone
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Deleting this knowledge base will permanently remove all documents, settings, and
            chat history. This action cannot be undone.
          </p>
          <Button
            variant="destructive"
            className="w-full"
            onClick={() => setShowDialog(true)}
          >
            <AlertTriangle className="h-4 w-4 mr-2" />
            Delete Knowledge Base
          </Button>
        </CardContent>
      </Card>

      <AlertDialog open={showDialog} onOpenChange={setShowDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-destructive flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              Confirm Deletion
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{kbName}</strong>?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4">
            <p className="text-sm text-muted-foreground mb-2">
              This will permanently delete:
            </p>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1">
              <li>All documents and processed data</li>
              <li>All settings and configurations</li>
              <li>Chat assistant and conversation history</li>
              <li>All analytics and performance data</li>
            </ul>
            <p className="text-sm font-bold text-destructive mt-4">
              This action cannot be undone!
            </p>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleteKB.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteKB.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  Yes, Delete Permanently
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
